// src/utils/wellnessCorrelations.ts
import { supabase } from '@/integrations/supabase/client';

export interface CorrelationResult {
  activity_type: string;
  avg_minutes_low: number;
  avg_minutes_high: number;
  avg_mood_low?: number | null;
  avg_mood_high?: number | null;
  avg_il_low?: number | null;
  avg_il_high?: number | null;
  interpretation: string;
}

/**
 * Very simple impact helper:
 * - looks at one activity type (e.g. 'meditation')
 * - aggregates minutes per day over the last N days
 * - splits days into "low minutes" vs "high minutes" (median split)
 * - compares average mood on low vs high days
 *
 * This is not a statistical test, just a gentle directional reflection for the user.
 */
export async function getActivityImpactOnMoodAndIL(
  userId: string,
  activityType: string,
  days: number = 30
): Promise<CorrelationResult | null> {
  const today = new Date();
  const from = new Date();
  from.setDate(today.getDate() - days);
  const fromDate = from.toISOString().slice(0, 10);
  const toDate = today.toISOString().slice(0, 10);

  // 1. Aggregate activity minutes per day for this type
  const { data: activities, error: actError } = await supabase
    .from('wellness_activities')
    .select('duration_minutes, started_at')
    .eq('user_id', userId)
    .eq('activity_type', activityType)
    .gte('started_at', `${fromDate}T00:00:00.000Z`)
    .lte('started_at', `${toDate}T23:59:59.999Z`);

  if (actError) {
    console.error('Activity fetch error', actError);
    return null;
  }

  const minutesByDate = new Map<string, number>();
  for (const a of activities || []) {
    const startedAt = a.started_at as string;
    const date = startedAt.slice(0, 10); // 'YYYY-MM-DD'
    const prev = minutesByDate.get(date) || 0;
    minutesByDate.set(date, prev + (a.duration_minutes || 0));
  }

  if (minutesByDate.size === 0) {
    return null;
  }

  // 2. Get mood from daily_checkins (using daily_feeling mapped to numeric)
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, daily_feeling')
    .eq('user_id', userId)
    .gte('date', fromDate)
    .lte('date', toDate);

  if (checkinError) {
    console.error('Checkin fetch error', checkinError);
  }

  // Map daily_feeling to numeric mood score
  const feelingToScore: Record<string, number> = {
    'amazing': 5,
    'good': 4,
    'okay': 3,
    'struggling': 2,
    'crisis': 1
  };

  const moodByDate = new Map<string, number>();
  for (const c of checkins || []) {
    if (c.daily_feeling && feelingToScore[c.daily_feeling]) {
      moodByDate.set(c.date, feelingToScore[c.daily_feeling]);
    }
  }

  // Note: IL scores integration can be added when daily_il_scores table exists
  const ilByDate = new Map<string, number>();

  // 3. Build day-level data
  type DayData = { date: string; minutes: number; mood?: number; il?: number };

  const daysData: DayData[] = [];
  for (const [date, minutes] of minutesByDate.entries()) {
    daysData.push({
      date,
      minutes,
      mood: moodByDate.get(date),
      il: ilByDate.get(date)
    });
  }

  if (daysData.length < 4) {
    // not enough days to say anything meaningful
    return null;
  }

  // 4. Split into low vs high (median split on minutes)
  const sorted = [...daysData].sort((a, b) => a.minutes - b.minutes);
  const midIndex = Math.floor(sorted.length / 2);
  const low = sorted.slice(0, midIndex);
  const high = sorted.slice(midIndex);

  const avg = (arr: number[]) =>
    arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

  const avgMinutesLow = avg(low.map(d => d.minutes));
  const avgMinutesHigh = avg(high.map(d => d.minutes));

  const moodLowValues = low
    .filter(d => d.mood != null)
    .map(d => d.mood as number);
  const moodHighValues = high
    .filter(d => d.mood != null)
    .map(d => d.mood as number);

  const ilLowValues = low
    .filter(d => d.il != null)
    .map(d => d.il as number);
  const ilHighValues = high
    .filter(d => d.il != null)
    .map(d => d.il as number);

  const moodLow = avg(moodLowValues);
  const moodHigh = avg(moodHighValues);
  const ilLow = avg(ilLowValues);
  const ilHigh = avg(ilHighValues);

  let interpretation = '';

  if (avgMinutesHigh > avgMinutesLow + 5) {
    const label = activityType.replace(/_/g, ' ');

    if (!isNaN(moodLow) && !isNaN(moodHigh) && moodLowValues.length > 0 && moodHighValues.length > 0) {
      const moodDiff = moodHigh - moodLow;
      if (Math.abs(moodDiff) > 0.3) {
        interpretation += `On days with more ${label}, your mood tends to be about ${moodDiff.toFixed(
          1
        )} points ${
          moodDiff > 0 ? 'higher' : 'lower'
        } on average. `;
      }
    }

    if (!isNaN(ilLow) && !isNaN(ilHigh) && ilLowValues.length > 0 && ilHighValues.length > 0) {
      const ilDiff = ilHigh - ilLow;
      if (Math.abs(ilDiff) > 2) {
        interpretation += `On higher ${label} days, your Inflammatory Load score tends to be about ${Math.abs(
          ilDiff
        ).toFixed(1)} points ${
          ilDiff < 0 ? 'lower (a potentially helpful shift)' : 'higher'
        }. `;
      }
    }
  }

  return {
    activity_type: activityType,
    avg_minutes_low: avgMinutesLow,
    avg_minutes_high: avgMinutesHigh,
    avg_mood_low: moodLowValues.length > 0 ? moodLow : null,
    avg_mood_high: moodHighValues.length > 0 ? moodHigh : null,
    avg_il_low: ilLowValues.length > 0 ? ilLow : null,
    avg_il_high: ilHighValues.length > 0 ? ilHigh : null,
    interpretation
  };
}
